export interface ErrorEntity {
  errorCode: string;
  errorMessage: string;
}

export interface BackEndError {
  message?: string;
  messageDetails?: string;
  errorCodes?: ErrorEntity[];
  status?: number;
}

export const managed_error_messages: Record<string, string> = {
  "4.1.001": "This operation is not allowed for account in this status!",
  "4.1.002": "Account does not exist",
  "4.1.003": "Account does not exist",
  "4.2.001": "Account does not exist",
  "4.2.002": "Unauthenticated 3rd party dkim domain",
  "4.2.004": "Inactive account subscription",
  "4.2.005":
    "The campaign has been scheduled and cannot be edited! If you want to edit it please Cancel Schedule",
  "4.2.006": "Unconfirmed sender details on starting campaign",
  "4.2.007": "Not assigned sender details on starting campaign",
  "4.2.009": "No active subscribers present. Please check your audience list.",
  "4.2.010":
    "The campaign has been scheduled and cannot be edited! If you want to edit it please Cancel Schedule",
  "4.2.012": "Test email limit exceeded for the campaign",
  "4.2.013": "Test email limit exceeded per day",
  "4.3.001": "Customer email already exists",
  "4.3.002": "Customer has invalid status for this operation",
  "4.4.001": "Template name already exists",
  "4.5.001": "Sender details' name already exists",
  "4.5.002":
    "Cannot update sender detail's email to unconfirmed if it's assigned to active campaign",
  "6.1.001": "Form name already exists",
  "6.1.002": "Invalid allowed domain",
  "7.4.001":
    "User is not allowed to generate new subject lines, limit exceeded",
  "8.4.001": "Default sender details must be confirmed",
  "8.4.002":
    "Automation is available only for specific integrations (ex: Shopify)",
  "8.5.001": "Automation does not exist",
};

export const managed_errors_codes = [
  "4.2.001",
  "4.2.002",
  "4.2.003",
  "4.2.004",
  "4.5.001",
  "4.5.002",
  "6.1.001",
];

export function getErrorMessageText(
  errorCodes: ErrorEntity[]
): string | undefined {
  if (errorCodes.length === 0) return undefined;
  return errorCodes
    .filter((e) => managed_error_messages[e.errorCode])
    .map((e) => managed_error_messages[e.errorCode])
    .join("; ");
}

export function isManagedError(error?: ErrorEntity[]): boolean {
  return (
    (error?.filter((e) => managed_errors_codes.includes(e.errorCode))?.length ||
      0) > 0
  );
}

export const DEFAULT_ERROR_MESSAGE =
  "Looks like you got some connectivity issues, please reload the page.";

export async function extractBackendError(response: {
  [key: string]: any;
}): Promise<BackEndError> {
  if (response?.data instanceof Blob) {
    return {
      ...JSON.parse(await response?.data.text()),
      status: response?.status,
    };
  } else {
    return { ...response?.data, status: response?.status };
  }
}

export function getErrorMessage(
  error: BackEndError,
  skipStatuses = [401]
): string {
  let message = DEFAULT_ERROR_MESSAGE;
  if (error?.status === 401 && skipStatuses.includes(error?.status)) {
    return "";
  } else if (error?.status === 412 && error?.message) {
    message = error.message;
  }
  return message;
}

export function findErrorCode(errorCodes: ErrorEntity[], errorList: string[]) {
  return errorCodes.find((error) => errorList.includes(error.errorCode))
    ?.errorCode;
}

export class AppError extends Error {
  constructor(message: string) {
    super(message);
    this.message = message;
  }
}

export function extractErrorMessage(error: any, skipStatuses = [401]) {
  if (!error) {
    return;
  }
  if (error instanceof AppError) {
    return error.message;
  }
  if ("response" in error!) {
    const response = (error as any).response;
    const status = response?.status;
    const errorCodes = response?.data?.errorCodes;
    const message = getErrorMessageText(errorCodes) ?? response?.data.message;
    if (isManagedError(errorCodes) || skipStatuses.includes(error?.status)) {
      return undefined;
    }
    if (status === 412 && message) {
      return message;
    }
  }
  const errorCodes = (error as any)?.errorCodes;
  return errorCodes
    ? getErrorMessageText(errorCodes) ??
        (error.message || DEFAULT_ERROR_MESSAGE)
    : error.message || DEFAULT_ERROR_MESSAGE;
}
